import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Check, Sun, Droplets, Leaf, Scissors, ShoppingBasket, Calculator, BookOpen, Ruler } from 'lucide-react';
import { toast } from 'sonner';
import { storeApi } from '../../lib/api';
import { useCart, formatAUD } from './CartContext';
import { ToleranceBar, VarietyPhoto } from './VarietyCard';
import { CareGuide } from './CareGuide';
import { AreaCalculatorDialog } from './area/AreaCalculatorDialog';

const TRAITS = [
  { key: 'shade_tolerance', label: 'Shade tolerance', icon: Sun },
  { key: 'drought_tolerance', label: 'Drought tolerance', icon: Droplets },
  { key: 'wear_tolerance', label: 'Wear tolerance', icon: Leaf },
  { key: 'maintenance', label: 'Maintenance', icon: Scissors },
];

function Gallery({ variety }) {
  const [active, setActive] = useState(0);
  const photos = variety.photos || [];
  return (
    <div className="space-y-3" data-testid="variety-gallery">
      <div className="aspect-[4/3] rounded-2xl overflow-hidden">
        {photos.length > 0
          ? <img src={storeApi.photoUrl(photos[active])} alt={variety.name} className="w-full h-full object-cover" data-testid="gallery-main-photo" />
          : <VarietyPhoto variety={variety} className="w-full h-full" />}
      </div>
      {photos.length > 1 && (
        <div className="grid grid-cols-5 gap-2">
          {photos.map((p, i) => (
            <button key={p} onClick={() => setActive(i)} className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${i === active ? 'border-[var(--s-terra)]' : 'border-transparent opacity-70 hover:opacity-100'}`} data-testid={`gallery-thumb-${i}`}>
              <img src={storeApi.photoUrl(p)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function VarietyDetailPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [variety, setVariety] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [area, setArea] = useState('');
  const [calcOpen, setCalcOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const preset = parseFloat(params.get('area'));
    storeApi.getVariety(slug).then((r) => { setVariety(r.data); setArea(String(preset > 0 ? preset : (r.data.min_order_m2 || 10))); }).catch(() => setNotFound(true));
  }, [slug, params]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-32 text-center" data-testid="variety-not-found">
        <h1 className="text-3xl font-bold mb-4">We couldn't find that variety</h1>
        <Link to="/" className="store-btn store-btn-dark">Back to our range</Link>
      </div>
    );
  }
  if (!variety) return <div className="max-w-7xl mx-auto px-5 py-20"><div className="h-96 rounded-2xl animate-pulse" style={{ background: 'var(--s-sand)' }} /></div>;

  const qty = parseFloat(area) || 0;
  const withWaste = Math.ceil(qty * 1.05);
  const estimate = qty * variety.price_per_m2;
  const belowMin = qty > 0 && qty < (variety.min_order_m2 || 0);

  const handleAdd = (goToOrder) => {
    if (qty <= 0) { toast.error('Enter the area you need in square metres'); return; }
    if (belowMin) { toast.error(`Minimum order is ${variety.min_order_m2} m²`); return; }
    addItem(variety, qty);
    toast.success(`${qty} m² of ${variety.name} added`);
    if (goToOrder) navigate('/shop/order');
  };

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 lg:py-16" data-testid="variety-detail">
      <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium mb-8 hover:underline" style={{ color: 'var(--s-green)' }} data-testid="back-to-range">
        <ArrowLeft className="h-4 w-4" /> All varieties
      </Link>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        <Gallery variety={variety} />

        <div>
          <p className="store-eyebrow mb-3">{variety.in_stock ? 'Available now' : 'Currently unavailable'}</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4" data-testid="variety-name">{variety.name}</h1>
          <p className="text-2xl font-heading font-bold mb-6" style={{ color: 'var(--s-green)' }} data-testid="variety-price">
            {formatAUD(variety.price_per_m2)} <span className="text-base font-medium" style={{ color: 'var(--s-muted)' }}>per m² · min {variety.min_order_m2} m²</span>
          </p>
          {variety.description && <p className="text-base leading-relaxed mb-8" style={{ color: 'var(--s-muted)' }} data-testid="variety-description">{variety.description}</p>}

          {variety.features?.length > 0 && (
            <ul className="grid sm:grid-cols-2 gap-2.5 mb-8" data-testid="variety-features">
              {variety.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm"><Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: 'var(--s-green-3)' }} /> {f}</li>
              ))}
            </ul>
          )}

          <div className="grid grid-cols-2 gap-x-6 gap-y-4 mb-8 p-5 rounded-2xl bg-white" style={{ border: '1px solid var(--s-sand)' }}>
            {TRAITS.map((t) => (
              <div key={t.key}>
                <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--s-muted)' }}><t.icon className="h-3.5 w-3.5" /> {t.label}</p>
                <ToleranceBar level={variety[t.key]} testId={`trait-${t.key}`} />
                <p className="text-xs mt-1 capitalize" style={{ color: 'var(--s-green)' }}>{variety[t.key] || '—'}</p>
              </div>
            ))}
          </div>

          {variety.ideal_for && <p className="text-sm mb-4"><span className="font-semibold">Ideal for:</span> <span style={{ color: 'var(--s-muted)' }}>{variety.ideal_for}</span></p>}
          {(variety.care_laying || variety.care_watering) && (
            <a href="#care-guide" className="inline-flex items-center gap-2 text-sm font-semibold mb-8 hover:underline" style={{ color: 'var(--s-green)' }} data-testid="care-guide-link"><BookOpen className="h-4 w-4" /> Laying &amp; watering guide</a>
          )}

          {/* Calculator */}
          <div className="rounded-2xl p-6 text-white" style={{ background: 'var(--s-green)' }} data-testid="variety-calculator">
            <p className="flex items-center gap-2 font-heading font-bold mb-4"><Calculator className="h-5 w-5" /> How much do you need?</p>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <label className="flex-1 text-sm">
                <span className="flex items-center justify-between text-white/70 mb-1.5">Area (m²) <button type="button" onClick={() => setCalcOpen(true)} className="inline-flex items-center gap-1 text-xs font-semibold text-white underline-offset-2 hover:underline" data-testid="open-area-calculator"><Ruler className="h-3.5 w-3.5" /> Work out my area</button></span>
                <input type="number" min="0" step="1" value={area} onChange={(e) => setArea(e.target.value)} className="store-input text-black" data-testid="area-input" />
              </label>
              <div className="flex-1 text-sm">
                <span className="block text-white/70 mb-1.5">Estimated turf cost</span>
                <p className="text-2xl font-heading font-bold" data-testid="estimate-total">{formatAUD(estimate)}</p>
              </div>
            </div>
            <p className="text-xs text-white/60 mt-3">
              {belowMin ? <span className="text-orange-200">Minimum order is {variety.min_order_m2} m².</span> : <>Tip: order about <strong>{withWaste} m²</strong> to allow 5% for cuts and edges. Delivery is quoted separately once we confirm your address.</>}
            </p>
            <div className="flex flex-wrap gap-3 mt-5">
              <button onClick={() => handleAdd(false)} disabled={!variety.in_stock} className="store-btn store-btn-ghost" data-testid="add-to-order-btn"><ShoppingBasket className="h-4 w-4" /> Add to order</button>
              <button onClick={() => handleAdd(true)} disabled={!variety.in_stock} className="store-btn store-btn-primary" data-testid="order-now-btn">Order now</button>
            </div>
          </div>
        </div>
      </div>

      <CareGuide variety={variety} />
      <AreaCalculatorDialog open={calcOpen} onOpenChange={setCalcOpen} onUse={(m2) => setArea(String(m2))} />
    </div>
  );
}
